import { useQuery } from '@tanstack/react-query';

import { getData } from '@/lib/api/client-v2';
import { isFleetUserName } from '@/lib/contracts';
import { queryKeys } from '@/lib/query-keys';
import { getSession, updateSession } from '@/lib/storage';
import { useUiStore } from '@/stores/ui-store';
import { ENDPOINTS } from '@/types/api/endpoints';
import type { User } from '@/types/api/user';

/**
 * Charge l'assuré connecté (`GET /api/v2/perso/me`).
 *
 * La v2 sert les mêmes champs que `GET /api/v1/user`, plus `is_fleet_user` et `features_disabled`.
 * Le guard `customers` du Pass admet les assurés et les pros COOLTRA, et refuse tout autre compte
 * (403) : le repli `customers → pros` et la garde COOLTRA de la v1 n'ont plus lieu d'être ici
 * (ils restent à la connexion classique, cf. `useLogin`).
 *
 * Un COOLTRA reste marqué `provider: 'pros'` en session, comme auparavant.
 */
export const fetchUser = async (): Promise<User> => {
    const user = await getData<User>(ENDPOINTS.me);

    if (isFleetUser(user) && getSession()?.provider !== 'pros') {
        updateSession({ provider: 'pros' });
    }

    return user;
};

/**
 * Vue restreinte à la flotte (COOLTRA).
 *
 * `is_fleet_user` vient du Pass. La règle historique sur le nom est conservée en garde-fou : le
 * Pass calcule `isPro() && isCooltra()`, or un COOLTRA a déjà été renvoyé sous le provider
 * `customers` en MFA — la vue complète s'était alors ouverte (cf. mémoire projet COOLTRA).
 */
export const isFleetUser = (user?: User): boolean => !!user?.is_fleet_user || isFleetUserName(user?.name);

/**
 * Utilisateur connecté, partagé par toute l'application.
 *
 * Le chargement initial est piloté par `PrivateRoute` ; les autres écrans lisent le cache.
 * `enabled` remplace la condition par défaut (session ouverte) : l'écran de connexion ne lit
 * l'utilisateur que si un jeton d'iframe est en session.
 */
export const useUser = (enabled?: boolean) => {
    const login = useUiStore((state) => state.login);

    return useQuery({
        queryKey: queryKeys.user,
        queryFn: async () => {
            try {
                const user = await fetchUser();
                useUiStore.getState().setLogin(true);
                return user;
            } catch (error) {
                useUiStore.getState().setLogin(false);
                throw error;
            }
        },
        enabled: enabled ?? (login || !!getSession()?.authenticated),
        staleTime: Infinity,
        retry: false,
    });
};

/** Utilisateur déjà chargé (les routes privées ne s'affichent qu'une fois celui-ci connu). */
export const useCurrentUser = (): User => useUser().data ?? {};

/** Vue restreinte aux fonctionnalités flotte (COOLTRA). */
export const useIsFleetUser = (): boolean => isFleetUser(useUser().data);
